import styles from './radio-group.module.css';
import { clsx } from 'clsx';
import { useField } from '@/components/form';
import { ErrorField } from '../error-field';
import { ReactNode } from 'react';
import { InputLabel, RequiredLabel } from './input-label';
import { MaterialSymbolIcon } from '../material-symbol-link';
import { FieldSet } from './field-set';

interface RadioGroupProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: RequiredLabel;
  subLabel?: ReactNode;
  readersOnlyLabel?: boolean;
  defaultCheckedValue?: string;
  options: { value: string; label: string; key?: string; icon?: ReactNode; className?: string }[];
  name: string;
  className?: string;
  fieldSetClassName?: string;
  variant?: 'selector' | 'toggle' | 'dropdown' | 'selectorCard' | 'chip';
}

export function RadioGroup({
  options,
  name,
  label,
  subLabel,
  readersOnlyLabel,
  defaultCheckedValue,
  className,
  fieldSetClassName,
  onChange,
  variant = 'selector',
  ...restProps
}: RadioGroupProps) {
  const { fieldProps, error } = useField(name, restProps);

  return (
    <FieldSet className={clsx(styles.radioGroup, fieldSetClassName)}>
      <InputLabel
        label={label}
        subLabel={subLabel}
        required={fieldProps.required}
        as="legend"
        readersOnly={readersOnlyLabel}
      />
      <div className={clsx(styles.formRadioGroup, className)}>
        {options.map((option) => {
          const key = option.key ?? option.value;

          return (
            <div key={key} className={clsx(styles.radio, styles[variant])}>
              <input
                {...fieldProps}
                className={clsx(styles.radioInput, fieldProps.className)}
                type="radio"
                value={option.value}
                id={key}
                defaultChecked={
                  defaultCheckedValue ? defaultCheckedValue === option.value : undefined
                }
                onChange={(e) => {
                  fieldProps.onChange(e);
                  onChange?.(e);
                }}
              />

              <label className={clsx(styles.radioLabel, option.className)} htmlFor={key}>
                {option.icon && <div className={styles.iconContainer}>{option.icon}</div>}
                <MaterialSymbolIcon name="check" className={styles.checkIcon} weight={600} />
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      <ErrorField error={error} />
    </FieldSet>
  );
}
